/**
 * The second way into an empty thread, beside Add Message: describe the situation, and the
 * scenario type, the world and the conversation arrive on their own, one after the other.
 * The trigger is the pill in the reset state; the panel is the box it opens, which becomes a
 * one-line progress while the flow runs and leaves with the first turn.
 */

import { useEffect, useRef, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { Loader2, PenLine, Wand2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { PillButton } from '../ui/pill-button';
import { describeFlowAtom, IDLE_DESCRIBE_FLOW, startDescribeFlowAtom } from './describe-flow';

const MAX_SITUATION_CHARS = 1000;

const PROGRESS = {
  type: 'Understanding the situation…',
  world: 'Setting up the world…',
  thread: 'Writing the conversation…',
} as const;

export const DescribeTrigger = () => {
  const [flow, setFlow] = useAtom(describeFlowAtom);
  if (flow.status !== 'idle') return null;
  return (
    <PillButton dashed onClick={() => setFlow({ ...flow, status: 'editing' })}>
      <PenLine className='h-3.5 w-3.5' />
      Describe a situation
    </PillButton>
  );
};

export const DescribePanel = () => {
  const [flow, setFlow] = useAtom(describeFlowAtom);
  const start = useSetAtom(startDescribeFlowAtom);
  const [text, setText] = useState('');
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const editing = flow.status === 'editing' || flow.status === 'error';
  const trimmed = text.trim();

  useEffect(() => {
    if (editing) areaRef.current?.focus();
  }, [editing]);
  // A reset or a finished flow leaves no draft behind for the next box.
  useEffect(() => {
    if (flow.status === 'idle') setText('');
  }, [flow.status]);

  if (flow.status === 'idle') return null;

  if (flow.status === 'type' || flow.status === 'world' || flow.status === 'thread') {
    return (
      <p className='flex items-center justify-center gap-2 text-xs text-gray-800 animate-in fade-in-0 duration-300'>
        <Loader2 className='size-3.5 animate-spin text-indigo-500' aria-hidden />
        {PROGRESS[flow.status]}
      </p>
    );
  }

  const submit = () => {
    if (trimmed) void start(trimmed);
  };

  return (
    <div className='mx-auto w-full max-w-md rounded-xl border border-gray-500 bg-white p-3 animate-in fade-in-0 slide-in-from-bottom-1 duration-300'>
      <Textarea
        ref={areaRef}
        value={text}
        maxLength={MAX_SITUATION_CHARS}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setFlow(IDLE_DESCRIBE_FLOW);
          }
        }}
        placeholder='The customer has an active monthly subscription and writes in to cancel it'
        className='min-h-20 resize-y border-0 bg-transparent p-0 text-sm shadow-none placeholder:text-text-disabled focus-visible:ring-0'
      />
      {flow.error && (
        <p role='alert' className='mt-1 text-xs text-destructive'>
          {flow.error}
        </p>
      )}
      <div className='mt-1.5 flex items-center justify-end gap-2'>
        <PillButton variant='ghost' onClick={() => setFlow(IDLE_DESCRIBE_FLOW)}>
          Cancel
        </PillButton>
        <PillButton variant='primary' disabled={!trimmed} onClick={submit}>
          <Wand2 className='h-3.5 w-3.5' />
          Create scenario
        </PillButton>
      </div>
    </div>
  );
};
